import { EXPLORE_ROUTES } from "../homeDashboard/exploreRoutes";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../dashboard/Dashboard.css";
import "../DashboardQuestionBank.css";
import "../homeDashboard/HomeDashboard.css";
import "../homeDashboard/DashboardSkeleton.css";
import HomeDashboardLayout from "../homeDashboard/HomeDashboardLayout";
import TopicCard from "./TopicCard";
import { useQuestionBankTopics } from "../hooks/useQuestionBankTopics";
import { useQuestionBankTopicToggle } from "../hooks/useQuestionBankTopicToggle";
import {
  buildQuestionBankBackTarget,
  buildQuestionBankFlowState,
  buildQuestionBankSubjectsState,
  getStoredQuestionBankFlowState,
  persistQuestionBankFlowState,
} from "./qbankFlowState";

const LECTURES_CATEGORY_ID = 3;
const NOTES_CATEGORY_ID = 4;
const FLASH_CARDS_CATEGORY_ID = 9;
const PODCAST_CATEGORY_ID = 12;

const QuestionBank = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedFlowState = React.useMemo(() => getStoredQuestionBankFlowState(), []);
  const flowState = React.useMemo(
    () => buildQuestionBankFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const topicRequestParams = flowState?.topicRequestParams || null;
  const selectedSubject = flowState?.selectedSubject || null;
  const [currentPage, setCurrentPage] = React.useState(Number(flowState?.topicRequestParams?.page || 1));
  const resolvedTopicRequestParams = React.useMemo(
    () => ({
      ...(topicRequestParams || {}),
      page: currentPage,
    }),
    [currentPage, topicRequestParams]
  );
  const {
    data: topicData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuestionBankTopics(resolvedTopicRequestParams, {
    enabled: Boolean(resolvedTopicRequestParams?.subject_id && resolvedTopicRequestParams?.category_id && resolvedTopicRequestParams?.state_id),
  });
  const [subtopicsEnabled, setSubtopicsEnabled] = React.useState(false);
  const [hasSubtopicToggleOverride, setHasSubtopicToggleOverride] = React.useState(false);
  const subTopicToggleMutation = useQuestionBankTopicToggle({
    subject_id: resolvedTopicRequestParams?.subject_id,
    category_id: resolvedTopicRequestParams?.category_id,
    state_id: resolvedTopicRequestParams?.state_id,
  });
  const isNotesCategory = Number(resolvedTopicRequestParams?.category_id || 0) === NOTES_CATEGORY_ID;
  const isLecturesCategory = Number(resolvedTopicRequestParams?.category_id || 0) === LECTURES_CATEGORY_ID;
  const isFlashCardsCategory = Number(resolvedTopicRequestParams?.category_id || 0) === FLASH_CARDS_CATEGORY_ID;
  const isPodcastCategory = Number(resolvedTopicRequestParams?.category_id || 0) === PODCAST_CATEGORY_ID;
  const isSubTopicToggleVisible = Number(topicData?.subTopicVisibilityStatus) === 1;
  const isSubTopicToggleSaving = subTopicToggleMutation.isPending;
  const isTopicPending = isLoading || isFetching;
  const isExamCourseFlow = Boolean(flowState?.examFlow);
  const hasForcedSubtopicFlow =
    Number(topicData?.forceTopicsEnabled || 0) === 1 &&
    Number(topicData?.forceSubtopicsEnabled || 0) === 1;

  const topicScreenState = React.useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        topicRequestParams: resolvedTopicRequestParams,
        qbankBackTarget:
          flowState?.qbankBackTarget || buildQuestionBankBackTarget(EXPLORE_ROUTES.subjects, buildQuestionBankSubjectsState(flowState)),
      }),
    [flowState, resolvedTopicRequestParams]
  );

  React.useEffect(() => {
    persistQuestionBankFlowState(topicScreenState);
  }, [topicScreenState]);

  React.useEffect(() => {
    if (!hasSubtopicToggleOverride) {
      setSubtopicsEnabled(Number(topicData?.subTopicToggleStatus) === 1);
    }
  }, [hasSubtopicToggleOverride, topicData?.subTopicToggleStatus]);

  React.useEffect(() => {
    if (Number(topicData?.subTopicVisibilityStatus) === 0) {
      setSubtopicsEnabled(false);
      setHasSubtopicToggleOverride(false);
    }
  }, [topicData?.subTopicVisibilityStatus]);

  React.useEffect(() => {
    setHasSubtopicToggleOverride(false);
  }, [resolvedTopicRequestParams?.category_id, resolvedTopicRequestParams?.subject_id, resolvedTopicRequestParams?.state_id, resolvedTopicRequestParams?.topic_id]);

  React.useEffect(() => {
    if (!topicRequestParams) {
      navigate(EXPLORE_ROUTES.subjects, {
        replace: true,
        state: buildQuestionBankSubjectsState(flowState),
      });
    }
  }, [flowState, navigate, topicRequestParams]);

  if (!topicRequestParams) {
    return null;
  }

  const handleSubTopicToggleChange = async (event) => {
    const nextChecked = event.target.checked;

    setSubtopicsEnabled(nextChecked);
    setHasSubtopicToggleOverride(true);

    try {
      await subTopicToggleMutation.mutateAsync({
        category_id: Number(topicRequestParams?.category_id || 2),
        sub_topic_toogle_status: nextChecked ? 1 : 0,
        ...(isExamCourseFlow ? { exam_id: Number(flowState?.exams_id || flowState?.exam_id || 0) } : {}),
      });
    } catch (_error) {
      setSubtopicsEnabled(!nextChecked);
      setHasSubtopicToggleOverride(false);
    }
  };

  return (
    <>
      <style>
        {`
          .homeDashboard-wrapper .page-content {
            padding-right: 0;
          }
       .layout-wrapper.homeDashboard-wrapper {
         background: linear-gradient(to bottom, rgba(103, 78, 221, 0.05) 0%, #fff 100%)!important;
       }
        `}
      </style>

      <HomeDashboardLayout showHeader={false}>
        <section className="qb Qbank-TopicCard" aria-label="Question bank introduction">
          <div className="qb-bodys">
            <div className="d-flex align-items-center justify-content-center">
              <div className="qb-copy">

                <div className="qb-loc">
                  <i className="bi bi-geo-alt-fill" />
                  <span> {topicData?.stateName || selectedSubject?.stateName || "Himachal Pradesh"}</span>
                </div>

                <h1 className="qb-title">
                  <span>{topicData?.subjectName || selectedSubject?.title || "Subject"}</span>
                </h1>

                <p className="qb-sub">
                  {topicData?.categoryName || (isNotesCategory ? "Notes" : isLecturesCategory ? "Lectures" : isFlashCardsCategory ? "Flash Cards" : isPodcastCategory ? "Podcasts" : "QBank")}
                </p>

                {isSubTopicToggleVisible ? (
                  <div className="qb-topics-row">
                    <div className="qb-topics">
                      <span className="qb-topics-label">Sub Topics</span>

                      <label className={`qb-toggle ${subtopicsEnabled ? "is-on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={subtopicsEnabled}
                          onChange={handleSubTopicToggleChange}
                          aria-label="Toggle sub topics"
                          disabled={!isSubTopicToggleVisible || isSubTopicToggleSaving}
                        />

                        <span className="qb-toggle-track" aria-hidden="true">
                          <span className="qb-toggle-thumb" />
                        </span>
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <div className="innerContent dashboardExam">
          {isError ? (
            <div className="cs-status">
              {error?.response?.data?.message || error?.message || "Unable to load topics."}
            </div>
          ) : (
            <TopicCard
              topics={topicData?.topicList || []}
              isLoading={isTopicPending}
              requestParams={resolvedTopicRequestParams}
              navigationState={topicScreenState}
              subtopicsEnabled={subtopicsEnabled}
              forceSubtopicFlow={hasForcedSubtopicFlow}
              pagination={topicData?.pagination || {}}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default QuestionBank;

